export const NORM_ORGANIZATION_ID_KEY = 'normAppOrganizationId';
export const NORM_ORGANIZATION_NAME_KEY = 'normAppOrganizationName';
export const NORM_IS_INTERNAL_VIEW_KEY = 'normIsInternalView';

/** Optional deep-link query param; must match `/web/organizations/select` */
export const URL_ORGANIZATION_ID_QUERY_PARAM = 'organization_id';

export function pathnameWithoutOrganizationIdQuery(
  pathname: string,
  searchParams: { toString(): string },
): string {
  const next = new URLSearchParams(searchParams.toString());
  next.delete(URL_ORGANIZATION_ID_QUERY_PARAM);
  const qs = next.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/** Appends organization_id if missing (for forwarding deep links past /login). */
export function mergeOrganizationIdIntoPath(
  pathWithOptionalQuery: string,
  organizationId: string,
): string {
  const hashIndex = pathWithOptionalQuery.indexOf('#');
  const basePath =
    hashIndex === -1
      ? pathWithOptionalQuery
      : pathWithOptionalQuery.slice(0, hashIndex);
  const fragment =
    hashIndex === -1 ? '' : pathWithOptionalQuery.slice(hashIndex + 1);
  const qIndex = basePath.indexOf('?');
  const pathname = qIndex === -1 ? basePath : basePath.slice(0, qIndex);
  const existing = qIndex === -1 ? '' : basePath.slice(qIndex + 1);
  const params = new URLSearchParams(existing);
  if (params.has(URL_ORGANIZATION_ID_QUERY_PARAM)) {
    return pathWithOptionalQuery;
  }
  params.set(URL_ORGANIZATION_ID_QUERY_PARAM, organizationId);
  const query = params.toString();
  const nextPath = query ? `${pathname}?${query}` : pathname;
  return fragment ? `${nextPath}#${fragment}` : nextPath;
}

const hasLocalStorage = (): boolean => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    const storage = window.localStorage;
    return (
      typeof storage !== 'undefined' &&
      typeof storage.getItem === 'function' &&
      typeof storage.setItem === 'function' &&
      typeof storage.removeItem === 'function'
    );
  } catch {
    return false;
  }
};

export const getIsInternalView = (): boolean => {
  if (!hasLocalStorage()) {
    return false;
  }

  try {
    return window.localStorage.getItem(NORM_IS_INTERNAL_VIEW_KEY) === 'true';
  } catch {
    return false;
  }
};

export function defaultHeaders(
  token: string,
  organizationId: string | null,
): HeadersInit {
  if (organizationId === null) {
    throw new Error('Organization ID must not be null');
  }

  const isInternalView = getIsInternalView();

  return {
    Authorization: `Bearer ${token}`,
    'X-Organization-ID': organizationId,
    'X-Internal-View': isInternalView.toString(),
  };
}

export const setSessionOrganization = (
  id: string,
  name: string,
  isInternalView: boolean,
): void => {
  if (!hasLocalStorage()) {
    return;
  }

  window.localStorage.setItem(NORM_ORGANIZATION_ID_KEY, id);
  window.localStorage.setItem(NORM_ORGANIZATION_NAME_KEY, name);
  window.localStorage.setItem(
    NORM_IS_INTERNAL_VIEW_KEY,
    isInternalView.toString(),
  );
};

export interface Organization {
  id: string;
  name: string;
  isInternalView: boolean;
}

export const getSessionOrganization = (): Organization | null => {
  if (!hasLocalStorage()) {
    return null;
  }

  try {
    const id = window.localStorage.getItem(NORM_ORGANIZATION_ID_KEY);
    const name = window.localStorage.getItem(NORM_ORGANIZATION_NAME_KEY);
    const isInternalView =
      window.localStorage.getItem(NORM_IS_INTERNAL_VIEW_KEY) === 'true';

    if (id && name) {
      return {
        id: id,
        name: name,
        isInternalView: isInternalView,
      };
    }

    return null;
  } catch (error) {
    console.error('Failed to read session organization:', error);
    return null;
  }
};

export const clearSessionOrganization = (): void => {
  if (!hasLocalStorage()) {
    return;
  }

  window.localStorage.removeItem(NORM_ORGANIZATION_ID_KEY);
  window.localStorage.removeItem(NORM_ORGANIZATION_NAME_KEY);
  window.localStorage.removeItem(NORM_IS_INTERNAL_VIEW_KEY);
};
